import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ShoppingCart, Minus, Plus, CheckCircle, Leaf, ArrowRight } from 'lucide-react';
import { Product } from '../types';

interface ProductQuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onAddToCart: (product: Product, quantity: number) => void;
  onViewDetails: (product: Product) => void;
}

export const ProductQuickViewModal: React.FC<ProductQuickViewModalProps> = ({
  isOpen,
  onClose,
  product,
  onAddToCart,
  onViewDetails
}) => {
  const [quantity, setQuantity] = useState(1);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Reset quantity when product changes
  useEffect(() => {
    setQuantity(1);
  }, [product]);

  if (!isOpen || !product || !mounted) return null;

  const basePrice = product.price || 0;
  const totalPrice = basePrice * quantity;
  const formattedPrice = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(totalPrice);

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
    onClose();
  };

  const modalContent = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] md:max-h-[600px] animate-fade-in">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm text-gray-500 hover:text-red-500 transition-all backdrop-blur-sm"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Image Section */}
        <div className="w-full md:w-1/2 bg-earth-50 p-8 flex items-center justify-center relative">
          <img 
            src={product.image} 
            alt={product.name} 
            className="max-h-[250px] md:max-h-[400px] w-auto object-contain drop-shadow-lg"
          />
          <span className="absolute top-6 left-6 bg-turmeric-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
            {product.category}
          </span>
        </div>

        {/* Details Section */}
        <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col overflow-y-auto custom-scrollbar">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-herbal-900 mb-2">
            {product.name}
          </h2>
          <p className="text-turmeric-600 font-medium mb-4 text-sm md:text-base border-l-4 border-turmeric-500 pl-3">
            {product.purpose}
          </p>
          
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {product.description}
          </p>

          {product.ingredients && (
            <div className="bg-earth-50 p-3 rounded-xl border border-earth-100 mb-6">
              <div className="flex items-center gap-2 mb-1 text-xs font-bold text-herbal-800 uppercase tracking-wide">
                <Leaf className="h-3 w-3 text-turmeric-500" />
                Key Ingredients
              </div>
              <p className="text-xs text-gray-600 line-clamp-2">
                {product.ingredients}
              </p>
            </div>
          )}

          <div className="mt-auto">
            <div className="flex items-center justify-between mb-6 pt-4 border-t border-gray-100">
               <div>
                  <span className="text-xs font-bold text-gray-400 uppercase block mb-1">Total Price</span>
                  <span className="text-2xl font-bold text-herbal-900">{formattedPrice}</span>
               </div>

               {/* Quantity Selector */}
               <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                 <button 
                   onClick={() => setQuantity(q => Math.max(1, q - 1))}
                   className="p-2 hover:bg-gray-100 text-gray-500 transition-colors rounded-l-lg"
                 >
                   <Minus className="h-4 w-4" />
                 </button>
                 <span className="w-8 text-center font-bold text-gray-800">{quantity}</span>
                 <button 
                   onClick={() => setQuantity(q => q + 1)}
                   className="p-2 hover:bg-gray-100 text-gray-500 transition-colors rounded-r-lg"
                 >
                   <Plus className="h-4 w-4" />
                 </button>
               </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleAddToCart}
                className="flex-1 bg-herbal-800 hover:bg-herbal-900 text-white font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                <ShoppingCart className="h-5 w-5" />
                Add to Cart
              </button>
              
              <button 
                onClick={() => {
                  onViewDetails(product);
                  onClose();
                }}
                className="bg-white border-2 border-earth-200 text-gray-500 hover:text-herbal-800 hover:border-herbal-800 font-bold p-3.5 rounded-xl transition-all"
                title="View Full Details"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
               <CheckCircle className="h-3 w-3" />
               <span>In Stock & Ready to Ship</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};